"use client";

import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import type { HomeTrustSectionContent } from "@/features/home/types/home-page-content";

interface HomeTrustStripProps {
  content: HomeTrustSectionContent;
}

export function HomeTrustStrip({ content }: HomeTrustStripProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "center center"],
  });

  // Animations for the three images fanning out based on scroll
  const leftImageRotate = useTransform(scrollYProgress, [0, 1], [0, -12]);
  const leftImageX = useTransform(scrollYProgress, [0, 1], [50, -160]);
  const leftImageY = useTransform(scrollYProgress, [0, 1], [20, 10]);

  const rightImageRotate = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const rightImageX = useTransform(scrollYProgress, [0, 1], [-50, 160]);
  const rightImageY = useTransform(scrollYProgress, [0, 1], [20, 10]);

  const centerImageScale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  const centerImageY = useTransform(scrollYProgress, [0, 1], [40, 0]);

  return (
    <section
      id={content.id}
      ref={sectionRef}
      className="relative z-40 overflow-hidden rounded-t-[2rem] bg-background px-6 py-24 shadow-[0_-36px_90px_rgba(7,16,20,0.22)] md:rounded-t-[2.5rem]"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center">
        {/* Header Section */}
        <div className="mb-16 flex flex-col items-center text-center md:mb-24">
          <span className="mb-6 inline-block rounded-full border border-border bg-white px-4 py-1.5 text-xs font-bold tracking-widest text-primary uppercase shadow-sm">
            {content.eyebrow}
          </span>
          <h2 className="mb-6 font-serif text-4xl leading-[1.1] tracking-tight text-foreground md:text-5xl lg:text-[56px]">
            {content.title} <span className="italic">{content.titleEmphasis}</span>
          </h2>
          <p className="max-w-2xl text-base text-muted-foreground md:text-lg">
            {content.description}
          </p>
        </div>

        {/* Fanning Images Section */}
        <div className="relative mb-24 flex h-[300px] w-full max-w-3xl items-center justify-center md:mb-32 md:h-[450px]">
          {/* Left Image */}
          <motion.div
            style={{ rotate: leftImageRotate, x: leftImageX, y: leftImageY }}
            className="absolute z-10 h-[280px] w-[200px] origin-bottom-right overflow-hidden rounded-3xl shadow-2xl md:h-[380px] md:w-[280px]"
          >
            <div className="absolute inset-0 z-10 rounded-3xl bg-black/10" />
            <Image
              src="/homepage/clearity.webp"
              alt="Clarity through reflection"
              fill
              className="object-cover"
            />
          </motion.div>

          {/* Right Image */}
          <motion.div
            style={{ rotate: rightImageRotate, x: rightImageX, y: rightImageY }}
            className="absolute z-10 h-[280px] w-[200px] origin-bottom-left overflow-hidden rounded-3xl shadow-2xl md:h-[380px] md:w-[280px]"
          >
            <div className="absolute inset-0 z-10 rounded-3xl bg-black/10" />
            <Image
              src="/homepage/routine.webp"
              alt="A calming self-care routine"
              fill
              className="object-cover"
            />
          </motion.div>

          {/* Center Image */}
          <motion.div
            style={{ scale: centerImageScale, y: centerImageY }}
            className="absolute z-20 h-[320px] w-[220px] overflow-hidden rounded-3xl shadow-2xl md:h-[420px] md:w-[320px]"
          >
            <Image
              src="/homepage/heard.webp"
              alt="Feeling heard and supported"
              fill
              className="object-cover"
            />
          </motion.div>
        </div>

        {/* Stats / Features Grid */}
        <div className="grid w-full grid-cols-1 gap-8 border-t border-border pt-16 md:grid-cols-3 md:gap-4 md:pt-20">
          {content.stats.map((stat) => (
            <div
              className="flex flex-col items-center border-border px-4 text-center md:border-r last:md:border-r-0"
              key={stat.id}
            >
              <h4 className="mb-4 text-lg font-medium text-foreground">
                {stat.title} <span className="font-serif italic">{stat.titleEmphasis}</span>
              </h4>
              <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action Button */}
        <div className="mt-16 md:mt-24">
          <Link
            className="group flex items-center gap-3 rounded-full bg-secondary py-2 pr-2 pl-6 transition-colors duration-300 hover:bg-secondary/80"
            href={content.ctaHref}
          >
            <span className="text-sm font-medium text-secondary-foreground">
              {content.ctaLabel}
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary transition-transform duration-300 group-hover:scale-105">
              <ArrowUpRight className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
